import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, 
  UserPlus, 
  Shield, 
  Settings, 
  Mail, 
  Clock, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Search
} from 'lucide-react';
import { useApi } from '../services/api';

interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: 'admin' | 'detective';
  is_active: boolean;
  mfa_enabled: boolean;
  created_at: string;
  updated_at: string;
  last_login_at?: string;
}

interface Invitation {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  created_at: string;
  expires_at: string;
  created_by: string;
}

interface AdminStats {
  users: {
    total: number;
    active: number;
    admins: number;
    detectives: number;
  };
  invitations: {
    pending: number;
  };
  enquetes: {
    total: number;
    active: number;
  };
}

const Administration: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'users' | 'invitations' | 'stats'>('stats');
  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Filtres et recherche
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  
  // Modal pour l'invitation
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'detective'
  });

  const api = useApi();

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get<AdminStats>('/admin/stats');
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
      setError('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (searchTerm) queryParams.append('search', searchTerm);
      if (roleFilter) queryParams.append('role', roleFilter);
      if (statusFilter) queryParams.append('status', statusFilter);
      
      const response = await api.get<{ users: User[] }>(`/admin/users?${queryParams.toString()}`);
      if (response.success) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  }, [api, searchTerm, roleFilter, statusFilter]);

  const loadInvitations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get<Invitation[]>('/admin/invitations/pending');
      if (response.success) {
        setInvitations(response.data);
      }
    } catch (error) {
      console.error('Error loading invitations:', error);
      setError('Erreur lors du chargement des invitations');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const handleInviteUser = async () => {
    try {
      setLoading(true);
      const response = await api.post('/admin/users/invite', inviteForm);
      if (response.success) {
        setShowInviteModal(false);
        setInviteForm({ email: '', firstName: '', lastName: '', role: 'detective' });
        loadInvitations();
        loadStats();
        alert('Invitation envoyée avec succès !');
      }
    } catch (error: any) {
      console.error('Error inviting user:', error);
      setError(error.message || 'Erreur lors de l\'envoi de l\'invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      setLoading(true);
      const response = await api.post(`/admin/invitations/${invitationId}/resend`, {});
      if (response.success) {
        alert('Invitation renvoyée avec succès !');
        loadInvitations();
      }
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      setError(error.message || 'Erreur lors du renvoi de l\'invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: boolean) => {
    try {
      setLoading(true);
      const response = await api.put(`/admin/users/${userId}`, {
        isActive: !currentStatus
      });
      if (response.success) {
        loadUsers();
        loadStats();
      }
    } catch (error: any) {
      console.error('Error updating user status:', error);
      setError(error.message || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleDisplay = (role: string) => {
    return role === 'admin' ? 'Administrateur' : 'Détective';
  };

  const getRoleBadgeColor = (role: string) => {
    return role === 'admin' ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400';
  };

  // Charger les données au montage
  useEffect(() => {
    loadStats();
    if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'invitations') {
      loadInvitations();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Administration</h1>
            <p className="text-gray-400">Gestion des utilisateurs et du système</p>
          </div>
        </div>
        
        {activeTab === 'users' && (
          <button
            onClick={() => setShowInviteModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-osint-orange hover:bg-orange-600 rounded-lg text-white transition-colors"
          >
            <UserPlus className="w-5 h-5" />
            <span>Inviter un utilisateur</span>
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 flex items-center space-x-2">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <span className="text-red-400">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-400 hover:text-red-300"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-1 bg-osint-light-gray rounded-lg p-1">
        <button
          onClick={() => setActiveTab('stats')}
          className={`flex-1 flex items-center justify-center space-x-2 py-2 px-4 rounded text-sm font-medium transition-colors ${
            activeTab === 'stats'
              ? 'bg-red-600 text-white'
              : 'text-gray-300 hover:text-white'
          }`}
        >
          <Settings className="w-4 h-4" />
          <span>Statistiques</span>
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`flex-1 flex items-center justify-center space-x-2 py-2 px-4 rounded text-sm font-medium transition-colors ${
            activeTab === 'users'
              ? 'bg-red-600 text-white'
              : 'text-gray-300 hover:text-white'
          }`}
        >
          <Users className="w-4 h-4" />
          <span>Utilisateurs</span>
        </button>
        <button
          onClick={() => setActiveTab('invitations')}
          className={`flex-1 flex items-center justify-center space-x-2 py-2 px-4 rounded text-sm font-medium transition-colors ${
            activeTab === 'invitations'
              ? 'bg-red-600 text-white'
              : 'text-gray-300 hover:text-white'
          }`}
        >
          <Mail className="w-4 h-4" />
          <span>Invitations</span>
          {stats?.invitations?.pending && stats.invitations.pending > 0 ? (
            <span className="bg-osint-orange text-white rounded-full px-2 py-0.5 text-xs">
              {stats.invitations.pending}
            </span>
          ) : null}
        </button>
      </div>

      {/* Content */}
      {activeTab === 'stats' && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Statistiques des utilisateurs */}
          <div className="osint-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Utilisateurs</h3>
              <Users className="w-8 h-8 text-blue-400" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-400">Total</span>
                <span className="text-white font-semibold">{stats.users.total}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Actifs</span>
                <span className="text-green-400 font-semibold">{stats.users.active}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Admins</span>
                <span className="text-red-400 font-semibold">{stats.users.admins}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Détectives</span>
                <span className="text-blue-400 font-semibold">{stats.users.detectives}</span>
              </div>
            </div>
          </div>

          {/* Statistiques des invitations */}
          <div className="osint-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Invitations</h3>
              <Mail className="w-8 h-8 text-osint-orange" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-400">En attente</span>
                <span className="text-osint-orange font-semibold">{stats.invitations.pending}</span>
              </div>
            </div>
          </div>

          {/* Statistiques des enquêtes */}
          <div className="osint-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Enquêtes</h3>
              <Search className="w-8 h-8 text-purple-400" />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-400">Total</span>
                <span className="text-white font-semibold">{stats.enquetes.total}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">En cours</span>
                <span className="text-green-400 font-semibold">{stats.enquetes.active}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          {/* Filtres */}
          <div className="osint-card p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher un utilisateur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="osint-input pl-10 w-full"
                />
              </div>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="osint-input"
              >
                <option value="">Tous les rôles</option>
                <option value="admin">Administrateur</option>
                <option value="detective">Détective</option>
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="osint-input"
              >
                <option value="">Tous les statuts</option>
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
              </select>
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={loadUsers}
                className="flex items-center space-x-2 px-4 py-2 bg-osint-orange hover:bg-orange-600 rounded-lg text-white transition-colors"
              >
                <Search className="w-4 h-4" />
                <span>Rechercher</span>
              </button>
            </div>
          </div>

          {/* Liste des utilisateurs */}
          <div className="osint-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-osint-light-gray">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Utilisateur
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Rôle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      MFA
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Dernière connexion
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-osint-light-gray">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-osint-light-gray/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-white">
                            {user.first_name && user.last_name 
                              ? `${user.first_name} ${user.last_name}` 
                              : 'Nom non défini'
                            }
                          </div>
                          <div className="text-sm text-gray-400">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                          {getRoleDisplay(user.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {user.is_active ? (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-400" />
                          )}
                          <span className={`text-sm ${user.is_active ? 'text-green-400' : 'text-red-400'}`}>
                            {user.is_active ? 'Actif' : 'Inactif'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {user.mfa_enabled ? (
                            <Shield className="w-4 h-4 text-green-400" />
                          ) : (
                            <AlertTriangle className="w-4 h-4 text-yellow-400" />
                          )}
                          <span className={`text-sm ${user.mfa_enabled ? 'text-green-400' : 'text-yellow-400'}`}>
                            {user.mfa_enabled ? 'Activé' : 'Désactivé'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {user.last_login_at ? formatDate(user.last_login_at) : 'Jamais'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                            className={`p-1 rounded hover:bg-osint-light-gray ${
                              user.is_active ? 'text-red-400 hover:text-red-300' : 'text-green-400 hover:text-green-300'
                            }`}
                            title={user.is_active ? 'Désactiver' : 'Activer'}
                          >
                            {user.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Invitations Tab */}
      {activeTab === 'invitations' && (
        <div className="space-y-6">
          <div className="osint-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-osint-light-gray">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Nom
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Rôle
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Envoyée le
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Expire le
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-osint-light-gray">
                  {invitations.map((invitation) => (
                    <tr key={invitation.id} className="hover:bg-osint-light-gray/50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                        {invitation.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {invitation.first_name && invitation.last_name 
                          ? `${invitation.first_name} ${invitation.last_name}` 
                          : '-'
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(invitation.role)}`}>
                          {getRoleDisplay(invitation.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {formatDate(invitation.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4" />
                          <span>{formatDate(invitation.expires_at)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleResendInvitation(invitation.id)}
                          className="p-1 rounded hover:bg-osint-light-gray text-osint-orange hover:text-orange-300"
                          title="Renvoyer l'invitation"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'invitation */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="osint-card max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Inviter un utilisateur</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="osint-input w-full"
                  placeholder="detective@example.com"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Prénom
                  </label>
                  <input
                    type="text"
                    value={inviteForm.firstName}
                    onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                    className="osint-input w-full"
                    placeholder="John"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nom
                  </label>
                  <input
                    type="text"
                    value={inviteForm.lastName}
                    onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                    className="osint-input w-full"
                    placeholder="Doe"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Rôle
                </label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                  className="osint-input w-full"
                >
                  <option value="detective">Détective</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowInviteModal(false)}
                className="px-4 py-2 bg-osint-light-gray hover:bg-gray-600 rounded-lg text-white transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleInviteUser}
                disabled={!inviteForm.email || loading}
                className="flex items-center space-x-2 px-4 py-2 bg-osint-orange hover:bg-orange-600 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg text-white transition-colors"
              >
                <UserPlus className="w-4 h-4" />
                <span>{loading ? 'Envoi...' : 'Envoyer l\'invitation'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Administration;